import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  HelpCircle, ChevronDown, ChevronLeft, Zap, Shield,
  DollarSign, Clock, Gamepad2, Camera, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Platform",
      icon: Shield,
      color: "#7000FF",
      questions: [
        {
          q: "Is MatchStake gambling?",
          a: "No. MatchStake is a skill-based esports competition platform. Match outcomes are determined entirely by player skill and in-game performance, not by chance. Players compete head-to-head in video games where the better player wins. This is similar to how professional esports tournaments work."
        },
        {
          q: "How does MatchStake work?",
          a: "MatchStake connects players who want to compete in skill-based gaming matches. Players create or join matches, pay an entry fee in credits, play their game, and the winner receives a competition reward from the platform. All outcomes depend on player skill, not luck."
        },
        {
          q: "Is MatchStake legal?",
          a: "Skill-based gaming competitions are legal in most jurisdictions as they are not considered gambling. However, regulations vary by location. By using MatchStake, you confirm that participating in skill-based competitions is legal in your jurisdiction."
        },
        {
          q: "How old do I need to be to use MatchStake?",
          a: "You must be at least 18 years old to use MatchStake. Age verification may be required for withdrawals."
        }
      ]
    },
    {
      category: "Matches",
      icon: Gamepad2,
      color: "#00F0FF",
      questions: [
        {
          q: "What games are supported?",
          a: "MatchStake currently supports popular competitive games including Madden NFL, NBA 2K, EA Sports FC (FIFA), Call of Duty, Fortnite, and Rocket League. You can also create custom game matches for other titles."
        },
        {
          q: "How are match results verified?",
          a: "Players submit their match results after competing. Screenshot proof can be uploaded to verify outcomes. If both players agree on the result, the match is completed immediately. If there's a dispute, our admin team reviews the evidence and makes a final decision."
        },
        {
          q: "What happens if players disagree on match results?",
          a: "When players submit conflicting results (e.g., both claim victory), the match is flagged as 'Disputed.' Our administrative team reviews the proof submitted by both players and makes a fair determination. The winner receives the competition reward, or if no clear winner can be determined, both players may be refunded."
        },
        {
          q: "How long do I have to complete a match?",
          a: "Matches have a 2-hour timer that begins when both players confirm participation. You must submit your results before the timer expires. If the timer runs out, the match may be frozen for admin review."
        },
        {
          q: "What skill levels are available?",
          a: "Players can select from four skill levels when creating or joining matches: Beginner, Intermediate, Advanced, and Professional. This helps ensure fair matchups. While you can join matches with different skill levels, a warning will be displayed if there's a significant skill gap."
        }
      ]
    },
    {
      category: "Proof & Results",
      icon: Camera,
      color: "#CCFF00",
      questions: [
        {
          q: "Do I need to submit proof for every match?",
          a: "Proof submission is recommended but not always required. If both players agree on the result, no proof is necessary. However, if there's a dispute, having proof significantly helps resolve it in your favor."
        },
        {
          q: "What counts as valid proof?",
          a: "Valid proof includes clear screenshots of the final game score, result screen, or match summary. The proof should clearly show both players' names/gamertags and the final outcome. Screenshots of in-game stats or victory screens work best."
        },
        {
          q: "Can we skip proof submission?",
          a: "Yes, both players can mutually agree to bypass proof submission. This is useful for trusted opponents or when both players agree on the outcome without needing verification."
        }
      ]
    },
    {
      category: "Payments",
      icon: DollarSign,
      color: "#00FF94",
      questions: [
        {
          q: "How do I add funds to my wallet?",
          a: "You can deposit funds to your MatchStake wallet using major credit/debit cards through our secure payment processor. Deposits are instant and appear in your wallet immediately."
        },
        {
          q: "What entry fees are available?",
          a: "Entry fees range from $5 to $100, with preset tiers at $5, $10, $15, $20, $25, $50, $75, and $100. The minimum entry fee is $5."
        },
        {
          q: "What fees does MatchStake charge?",
          a: "MatchStake charges a platform service fee for organizing and hosting competitions. This fee is factored into the competition reward structure. For example, in a $10 entry match, the winner receives a reward of $18.40."
        },
        {
          q: "How do withdrawals work?",
          a: "Withdrawals require administrative approval for security purposes. The minimum withdrawal amount is $25. Processing typically takes 24-72 hours. Once approved, funds are sent to your designated payment method."
        },
        {
          q: "How long do withdrawals take?",
          a: "Withdrawal processing typically takes 24-72 hours. All withdrawals are reviewed by our team before processing to ensure security and compliance. You'll receive a notification when your withdrawal is approved."
        }
      ]
    },
    {
      category: "Account",
      icon: Users,
      color: "#FFB800",
      questions: [
        {
          q: "How do I create an account?",
          a: "You can sign up instantly using Google OAuth or create an account with your email and password. Account creation is free and only takes a few seconds."
        },
        {
          q: "What is the ELO rating system?",
          a: "MatchStake uses an ELO rating system to track player skill. All players start at 1200 ELO. Winning matches increases your rating, while losing decreases it. Higher-rated players are recognized on the leaderboard with tier badges like Diamond, Platinum, and Gold."
        },
        {
          q: "Can my account be suspended?",
          a: "Accounts may be suspended for violations of our terms of service, including cheating, submitting false results, or abusive behavior. Suspended users cannot participate in matches until the issue is resolved."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <Zap className="w-6 h-6 text-[#00F0FF]" />
            <span className="font-heading font-bold text-lg">MATCHSTAKE</span>
          </div>
          <Button onClick={() => navigate(-1)} variant="outline" className="btn-secondary">
            <ChevronLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 pt-24 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-2">Frequently Asked Questions</h1>
            <p className="text-white/50">Everything you need to know about MatchStake</p>
          </div>

          {/* Skill-Based Notice */}
          <div className="p-4 rounded-xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-8 text-center">
            <p className="text-[#00FF94] font-medium">
              MatchStake is a skill-based competitive gaming platform. Outcomes are determined by player skill, not chance.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-8">
            {faqs.map((category, catIndex) => (
              <div key={catIndex} className="glass-card rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-white/10 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${category.color}20` }}>
                    <category.icon className="w-5 h-5" style={{ color: category.color }} />
                  </div>
                  <h2 className="font-heading text-lg font-semibold">{category.category}</h2>
                </div>
                
                <div className="divide-y divide-white/5">
                  {category.questions.map((faq, qIndex) => {
                    const index = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === index;
                    
                    return (
                      <div key={qIndex}>
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : index)}
                          className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                        >
                          <span className="font-medium pr-4">{faq.q}</span>
                          <ChevronDown 
                            className={`w-5 h-5 text-white/50 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
                          />
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <p className="px-4 pb-4 text-white/60 leading-relaxed">
                                {faq.a}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact */}
          <div className="mt-12 text-center p-6 glass-card rounded-2xl">
            <h3 className="font-heading text-xl font-semibold mb-2">Still have questions?</h3>
            <p className="text-white/50 mb-4">Our support team is here to help.</p>
            <Button className="btn-primary">Contact Support</Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
